import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Field, Formik, Form } from "formik";
import { defaultCompare as naturalSort } from "logic/DefaultCompare";
import connect from "stores/connect";
import { Alert, ControlLabel, FormGroup, Col, Input, Row } from "components/bootstrap";
import { Select, Spinner, ReadOnlyFormGroup, ConfirmDialog, FormSubmit, FormikInput } from "components/common";
import NumberUtils from "util/NumberUtils";
import DocumentationLink from "components/support/DocumentationLink";
import AppConfig from "util/AppConfig";
import ChecksumTypes from "archive/logic/ChecksumTypes";
import CompressionTypes from "archive/logic/CompressionTypes";
import BackendTypes from "archive/logic/BackendTypes";
import ArchiveConfigStreamSelectionForm from "archive/components/ArchiveConfigStreamSelectionForm";
import { ArchiveConfigurationActions } from "archive/ArchiveConfigurationStore";
import ArchiveBackendsStore, { ArchiveBackendsActions } from "archive/ArchiveBackendsStore";
import { SEGMENT_COMPRESSIONS, SEGMENT_CHECKSUMS } from "archive/types";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import ArchiveRoutes from "../Routes";
const isCloud = AppConfig.isCloud();
const StyledForm = styled(Form)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.md};
  `
);
const StyledAlert = styled(Alert)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.xs};
  `
);
const validateArchiveConfigForm = (values) => {
  const errors = {};
  if (!values.restrict_to_leader && !(values.parallel_restore_jobs > 0)) {
    errors.parallel_restore_jobs = "At minimum 1 job is required.";
  }
  if (!(values.restore_index_batch_size > 0)) {
    errors.restore_index_batch_size = "Needs to be at least 1.";
  }
  if (!(values.max_segment_size > 0)) {
    errors.max_segment_size = "Needs to be at least 1.";
  }
  if (!(values.retention_time >= 0)) {
    errors.retention_time = "Needs to be at least 0.";
  }
  if (!(values.archive_failure_threshold > 0)) {
    errors.archive_failure_threshold = "Needs to be at least 1.";
  }
  if (!SEGMENT_COMPRESSIONS.includes(values.segment_compression_type)) {
    errors.segment_compression_type = "Please select a valid compression type.";
  }
  if (!SEGMENT_CHECKSUMS.includes(values.segment_checksum_type)) {
    errors.segment_checksum_type = "Please select a valid checksum type.";
  }
  const requiredFields = [
    "backend_id",
    "parallel_restore_jobs",
    "max_segment_size",
    "restore_index_batch_size",
    "archive_failure_threshold",
    "retention_time"
  ];
  requiredFields.forEach((field) => {
    if (values[field] === "" || typeof values[field] === "undefined") {
      errors[field] = "Is required.";
    }
  });
  return errors;
};
const PARALLEL_RESTORE_JOBS_DEFAULT = 1;
const ArchiveConfigForm = ({ backends = void 0 }) => {
  const [initialArchive, setInitialArchive] = useState(void 0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [retentionTimeChanged, setRetentionTimeChanged] = useState(false);
  const history = useHistory();
  const sendTelemetry = useSendTelemetry();
  useEffect(() => {
    ArchiveConfigurationActions.getConfig().then((config) => {
      setInitialArchive(config);
    });
    ArchiveBackendsActions.listBackends(1, 1e3);
  }, []);
  if (!initialArchive || !backends) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  const handleRestrictToLeaderChange = (setFieldValue, values, setFieldTouched) => {
    const newValue = !values.restrict_to_leader;
    setFieldValue("restrict_to_leader", newValue);
    if (newValue === true) {
      setFieldValue("parallel_restore_jobs", PARALLEL_RESTORE_JOBS_DEFAULT);
    }
    setFieldTouched("parallel_restore_jobs");
  };
  const updateConfiguration = (values) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ARCHIVE.CONFIGURATION_UPDATED, {
      app_pathname: "archive",
      app_section: "archive-configuration"
    });
    ArchiveConfigurationActions.saveConfig(values).then(() => {
      history.replace(ArchiveRoutes.LIST);
    });
  };
  const saveConfiguration = (values) => {
    if (retentionTimeChanged) {
      setShowConfirmDialog(true);
    } else {
      updateConfiguration(values);
    }
  };
  const abortSaving = () => {
    setShowConfirmDialog(false);
  };
  const sortedBackends = backends.backends.map((backend) => ({
    value: backend.id,
    label: `${backend.title} (${BackendTypes.getBackendType(backend.settings.type).label})`
  })).sort((a, b) => naturalSort(a.label.toLowerCase(), b.label.toLowerCase()));
  const retentionTimeString = (retentionTime) => {
    if (retentionTime === void 0 || Number(retentionTime) === 0) {
      return "unlimited";
    }
    return NumberUtils.formatNumber(retentionTime);
  };
  const currentBackend = (configuration) => backends.backends.find((backend) => backend.id === configuration.backend_id);
  const compressionTypesHelpText = /* @__PURE__ */ React.createElement("span", null, "Compression method to apply on archive files. Read the", " ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#compression-type", text: "documentation" }), " for more information on the different options.");
  const checksumTypesHelpText = /* @__PURE__ */ React.createElement("span", null, "Checksum method to apply on archive files. Read the", " ", /* @__PURE__ */ React.createElement(DocumentationLink, { page: "setup-archiving#checksum-type", text: "documentation" }), " for more information on the different options.");
  const sortedCompressionTypes = [...CompressionTypes.compressionTypes].sort(
    (t1, t2) => naturalSort(t1.label.toLowerCase(), t2.label.toLowerCase())
  );
  const onCancel = () => history.push(ArchiveRoutes.LIST);
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("h2", null, "Archives configuration"), /* @__PURE__ */ React.createElement(
    Formik,
    {
      initialValues: initialArchive,
      onSubmit: saveConfiguration,
      validate: validateArchiveConfigForm,
      validateOnMount: true
    },
    ({ isSubmitting, isValidating, setFieldValue, values, isValid, setFieldTouched }) => {
      var _a;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(StyledForm, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement("fieldset", null, isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Backend", value: (_a = currentBackend(values)) == null ? void 0 : _a.title }), /* @__PURE__ */ React.createElement(ReadOnlyFormGroup, { label: "Retention Time (days)", value: initialArchive.retention_time })), !isCloud && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Field, { name: "backend_id" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          name: "backend_id",
          bsStyle: error ? "error" : void 0,
          error,
          label: "Backend",
          required: true,
          help: "In which backend should the archives be stored?",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          Select,
          {
            placeholder: "Select Backend",
            clearable: false,
            options: sortedBackends,
            onChange: (selected) => onChange({ target: { value: selected, name } }),
            value
          }
        )
      )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Enable Multithreading"), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "parallelize_archive_creation",
          id: "parallelize_archive_creation",
          formGroupClassName: "col-sm-9",
          label: "Allow archiving to utilize multiple threads",
          type: "checkbox",
          help: /* @__PURE__ */ React.createElement("span", null, "Enable this option to allow Archiving tasks to use more than one Java thread per Search node.", /* @__PURE__ */ React.createElement("br", null), "This ", /* @__PURE__ */ React.createElement("strong", null, " can increase "), " both the rate of archiving, and", " ", /* @__PURE__ */ React.createElement("strong", null, " load on your Search cluster."), /* @__PURE__ */ React.createElement("p", null), "Note: the maximum number of parallel threads for Archiving is limited to the count of shards in the target Index, and by default is further capped at 50% of search node CPU core count.")
        }
      )), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, { className: "col-sm-3" }, "Restrict to leader node"), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          name: "restrict_to_leader",
          id: "restrict_to_leader",
          type: "checkbox",
          formGroupClassName: "col-sm-9",
          onChange: () => handleRestrictToLeaderChange(setFieldValue, values, setFieldTouched),
          label: "Restrict Archive restore jobs to run only on the leader node",
          help: "Only turn this flag off if all nodes operate on the same shared filesystem for the Archiving backend.\n                    Or if a S3 backend is used."
        }
      )), !values.restrict_to_leader && /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "parallel_restore_jobs",
          id: "parallel_restore_jobs",
          label: "Parallel Restore Jobs",
          min: 1,
          help: /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("span", null, "Number of restore jobs that should run in parallel."), " ", /* @__PURE__ */ React.createElement(StyledAlert, { bsStyle: "warning" }, "The option to run Restore Jobs in parallel is currently a ", /* @__PURE__ */ React.createElement("strong", null, "beta feature"), ", and", " ", /* @__PURE__ */ React.createElement("strong", null, "we advise that it should only be used for testing purposes or where explicitly recommended by Graylog Professional Services"), ". Maximum parallelism is limited to the number of Graylog nodes. Higher values may place extraordinary indexing pressure on Opensearch, which risks placing the Opensearch cluster in a red state.")),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "max_segment_size",
          id: "max_segment_size",
          label: "Max Segment Size",
          min: 1,
          help: /* @__PURE__ */ React.createElement("span", null, "Maximum size for each message segment in ", /* @__PURE__ */ React.createElement("strong", null, "bytes"), "."),
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      ), /* @__PURE__ */ React.createElement(Field, { name: "segment_compression_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a2;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_compression_type",
            label: "Compression Type",
            help: compressionTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: "Select Compression Type",
              options: sortedCompressionTypes,
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a2 = CompressionTypes.getCompressionType(value)) == null ? void 0 : _a2.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(Field, { name: "segment_checksum_type" }, ({ field: { name, value, onChange }, meta: { error } }) => {
        var _a2;
        return /* @__PURE__ */ React.createElement(
          Input,
          {
            bsStyle: error ? "error" : void 0,
            error,
            name: "segment_checksum_type",
            label: "Checksum Type",
            help: checksumTypesHelpText,
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9"
          },
          /* @__PURE__ */ React.createElement(
            Select,
            {
              placeholder: "Select Checksum Type",
              options: [...ChecksumTypes.checksumTypes],
              onChange: (selected) => onChange({ target: { value: selected, name } }),
              value: (_a2 = ChecksumTypes.getChecksumType(value)) == null ? void 0 : _a2.value,
              clearable: false
            }
          )
        );
      }), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "restore_index_batch_size",
          id: "restore_index_batch_size",
          label: "Restore index batch size",
          help: "When restoring an archive, what batch size should be used to re-import the data?",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          name: "archive_failure_threshold",
          id: "archive_failure_threshold",
          label: "Failure notification threshold",
          help: "Number of consecutive failed attempts before showing an error notification",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          min: 1,
          required: true
        }
      ), /* @__PURE__ */ React.createElement(
        FormikInput,
        {
          type: "number",
          min: 0,
          name: "retention_time",
          id: "retention_time",
          label: "Retention Time (days)",
          onChange: ({ target: { value } }) => {
            setRetentionTimeChanged(initialArchive.retention_time !== Number(value));
            setFieldValue("retention_time", value);
          },
          help: "Define how many days to keep archives before deleting them. When 0 is set, automatic deletion is disabled.",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9",
          required: true
        }
      )), /* @__PURE__ */ React.createElement(Field, { name: "excluded_streams" }, ({ field: { name, value, onChange }, meta: { error } }) => /* @__PURE__ */ React.createElement(
        Input,
        {
          bsStyle: error ? "error" : void 0,
          error,
          name: "excluded_streams",
          label: "Streams to archive",
          help: "Select streams that should be included in the archive. New streams will be archived by default.",
          labelClassName: "col-sm-3",
          wrapperClassName: "col-sm-9"
        },
        /* @__PURE__ */ React.createElement(
          ArchiveConfigStreamSelectionForm,
          {
            excludedStreams: value,
            updateExcludedStreams: (selected) => onChange({ target: { value: selected, name } })
          }
        )
      )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { smOffset: 3, sm: 9 }, /* @__PURE__ */ React.createElement(
        FormSubmit,
        {
          submitButtonText: "Update configuration",
          disabledSubmit: isValidating || !isValid,
          isSubmitting,
          isAsyncSubmit: true,
          submitLoadingText: "Updating configuration...",
          onCancel
        }
      ))))), /* @__PURE__ */ React.createElement(
        ConfirmDialog,
        {
          show: showConfirmDialog,
          onConfirm: () => updateConfiguration(values),
          onCancel: abortSaving,
          title: "Are you sure?"
        },
        /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Archive retention time will be configured to ", retentionTimeString(values == null ? void 0 : values.retention_time), " days. Current configuration is ", retentionTimeString(initialArchive == null ? void 0 : initialArchive.retention_time), " days."), /* @__PURE__ */ React.createElement("p", null, "Deleted archives can't be restored."), /* @__PURE__ */ React.createElement("p", null, "Do you want to continue?"))
      ));
    }
  ));
};
export default connect(
  ArchiveConfigForm,
  {
    archiveBackends: ArchiveBackendsStore
  },
  ({ archiveBackends, ...otherProps }) => ({
    backends: archiveBackends.backends,
    ...otherProps
  })
);
